<template>
  <div class="video-screen-inner">
    <div class="record-time">
      <span v-if="peerState === 'connecting'" class="ps-4">{{$t('calling')}}</span>
      <span v-else class="ps-4">{{$t('call')}}</span>
    </div>

    <div class="video-call-action action-calls">
      <div></div>

      <ul class="center-action">
        <li class="d-none">
          <a
            class="mute-bt me-1"
            href="javascript:void(0);"
            @click="toggleMute"
            :title="mute ? $t('unmute_audio') : $t('mute_audio')"
            :class="muteButtonClass"
          >
            <i :class="mute ? 'feather-mic-off' : 'feather-mic'"></i>
          </a>
        </li>

        <li class="d-none">
          <a-tooltip :mouseEnterDelay="0" :mouseLeaveDelay="0"
                     :title="(!this.canCallWithVideo) ? 'Upgrade' : 'Video call'" placement="top"
          >
            <a
              :style="!this.canCallWithVideo ? 'background: #bdbdbd !important; cursor: not-allowed; color: #933030 !important;' : ''"
              class="mute-video me-1"
              href="javascript:void(0);"
              @click="toggleMuteVideo"
              :title="muteVideo ? $t('start_camera') : $t('stop_camera')"
              :class="muteButtonClassVideo"
            >
              <i :class="muteVideo ? 'feather-video-off' : 'feather-video'"></i>
            </a>
          </a-tooltip>
        </li>

        <li>
          <a href="javascript:;" class="call-end me-1" @click.prevent="EndCall">
            <i class="feather-phone-off"></i>
          </a>
        </li>
      </ul>

      <div class="d-none">
        <video id="localVideo" width="60px" height="60px" class="video-current-user"></video>
      </div>
    </div>

    <div class="video-avatar online active d-none">
      <video id="remoteVideo"></video>
      <audio id="remoteAudio" class="hidden d-none"></audio>
    </div>
  </div>
</template>

<script>
import { Tooltip, Button } from "ant-design-vue";
import {mapGetters} from "vuex";

export default {
  components: {
    "a-tooltip": Tooltip,
    "a-button": Button,
  },

  props: {
    user: {
      type: Object,
      required: true,
    }
  },

  watch: {
    onGoingCall: {
      handler() {
        if (this.onGoingCall) {
          this.StartCall();
        } else {
          this.callStarted = false;
        }
      },
      immediate: true,
    },
  },

  data() {
    return {
      _combinedStream: null,
      _localVideoStream: null,
      _localAudioStream: null,
      _remoteVideoStream: null,
      _remoteAudioStream: null,
      callStarted: false,
      showSidebar: false,
      mute: false,
      muteVideo: true,
      hideChatBar: false,
      localPeer: null,
      connection: null,
    };
  },

  computed: {
    ...mapGetters({
      currentChatUser: 'chat/currentChatUser',
      peerState: 'chat/peerState',
      onGoingCall: 'chat/onGoingCall',
      canCallWithAudio: 'subscriptions/canCallWithAudio',
      canCallWithVideo: 'subscriptions/canCallWithVideo',
    }),

    canCall() {
      return this.canCallWithAudio || this.canCallWithVideo;
    },

    muteButtonClass() {
      return this.mute ? "stop" : "";
    },

    muteButtonClassVideo() {
      return this.muteVideo ? "stop" : "";
    },
  },

  mounted() {
    this.$eventHub.$on('PeerConnectionClosed', this.EndCall);
    this.$eventHub.$on('PeerRefuseHandshake', this.gotRefused);
    this.setListeners();
  },

  unmounted() {
    this.$eventHub.$off('PeerConnectionClosed', this.EndCall);
    this.$eventHub.$off('PeerRefuseHandshake', this.gotRefused);
  },

  methods: {
    gotRefused() {
      this.EndCall();
    },

    async EndCall() {
      const stopTrack = track => {
        track.enabled = false;
        track.stop();
      };

      if (this._localVideoStream) {
        this._localVideoStream.getTracks().forEach(stopTrack);
        this._localVideoStream.getVideoTracks().forEach(stopTrack);
        this._localVideoStream.getAudioTracks().forEach(stopTrack);
      }

      if (this._localAudioStream) {
        this._localAudioStream.getTracks().forEach(stopTrack);
        this._localAudioStream.getVideoTracks().forEach(stopTrack);
        this._localAudioStream.getAudioTracks().forEach(stopTrack);
      }

      document.querySelector('#localVideo').srcObject = null;
      document.querySelector('#remoteVideo').srcObject = null;
      document.querySelector('#remoteAudio').srcObject = null;

      window.Peer.close();
      this.$emit('close');

      this._combinedStream = null;
      this._localVideoStream = null;
      this._remoteVideoStream = null;
      this._remoteVideoStream = null;
      this._remoteVideoStream = null;
      this.callStarted = false;
    },

    async StartCall() {
      if (!this.canCall || this.callStarted) {
        return;
      }

      this._combinedStream = null;
      this._localVideoStream = null;
      this._localAudioStream = null;
      this._remoteVideoStream = null;
      this._remoteAudioStream = null;
      this.callStarted = false;
      this.mute = false;
      this.muteVideo = false;

      const audioConstraints = {
        audio: true,
      };
      const videoConstraints = {
        audio: false,
        video: true,
      };

      let tracks = [];
      if (this.canCallWithAudio) {
        this._localAudioStream = await navigator.mediaDevices
          .getUserMedia(audioConstraints)
          .catch(e => alert('Audio/video devices not found. Please refresh the page and try again or check permissions settings.'));
        tracks = [...tracks, ...this._localAudioStream.getAudioTracks()];
      }

      if (this.canCallWithVideo) {
        this._localVideoStream = await navigator.mediaDevices
          .getUserMedia(videoConstraints)
          .catch(e => alert('Audio/video devices not found. Please refresh the page and try again or check permissions settings.'));
        tracks = [...tracks, ...this._localVideoStream.getVideoTracks()];
      }

      this._combinedStream = new MediaStream(tracks);

      this.showStream(true);

      if (window.Peer.initiatedByMe) {
        window.Peer.call(this._combinedStream, (remoteStream) => {
          this._remoteAudioStream = remoteStream.getAudioTracks()[0];
          this._remoteVideoStream = remoteStream.getVideoTracks()[0];

          this.showStream(false);
        });
      } else {
        window.Peer.onCall((call) => {
          call.answer(this._combinedStream, window.Peer.DefaultCallOptions);
          call.on('stream', (remoteStream) => {
            this._remoteAudioStream = remoteStream.getAudioTracks()[0];
            this._remoteVideoStream = remoteStream.getVideoTracks()[0];

            this.showStream(false);
          });
        });
      }

      this.callStarted = true;
    },

    showStream(currentUser = false) {
      if (currentUser && this.canCallWithVideo) {
        // Video
        if (this._localVideoStream) {
          document.querySelector('#localVideo').srcObject = this._localVideoStream;
          document.querySelector('#localVideo').autoplay = true;
          document.querySelector('#localVideo').muted = true;
          document.querySelector('#localVideo').onloadedmetadata = () => {
            document.querySelector('#localVideo').play();
          };
        }
      } else {
        // Video
        if (this.canCallWithVideo && this._remoteVideoStream) {
          document.querySelector('#remoteVideo').srcObject = new MediaStream([this._remoteVideoStream]);
          document.querySelector('#remoteVideo').autoplay = true;
          document.querySelector('#remoteVideo').muted = true;
          document.querySelector('#remoteVideo').onloadedmetadata = () => {
            document.querySelector('#remoteVideo').play();
          };
        }

        // Audio
        if (this.canCallWithAudio && this._remoteAudioStream) {
          document.querySelector('#remoteAudio').srcObject = new MediaStream([this._remoteAudioStream]);
          document.querySelector('#remoteAudio').onloadedmetadata = () => {
            document.querySelector('#remoteAudio').play();
          };
        }
      }
    },

    toggleSidebar() {
      this.showSidebar = !this.showSidebar;
      if (window.innerWidth > 991 && window.innerWidth < 1201) {
        this.hideChatBar = false;
      }
      if (window.innerWidth < 992) {
        this.hideChatBar = true;
      }
    },

    toggleMute() {
      if (!this.canCallWithAudio) {
        return;
      }

      this.mute = !this.mute;

      if (this._combinedStream) {
        if (this._combinedStream.getAudioTracks().length) {
          this._combinedStream.getAudioTracks()[0].enabled = !this.mute;
        }
      }
    },

    toggleMuteVideo() {
      if (!this.canCallWithVideo) {
        return;
      }

      this.muteVideo = !this.muteVideo;

      if (this._localVideoStream) {
        if (this._localVideoStream.getVideoTracks().length) {
          this._localVideoStream.getVideoTracks()[0].enabled = !this.muteVideo;
        }
      }
    },

    setListeners() {
      // Add an event listener for click on elements with class "dream_profile_menu"
      var dreamProfileMenuElements = document.getElementsByClassName("dream_profile_menu");
      for (var i = 0; i < dreamProfileMenuElements.length; i++) {
        dreamProfileMenuElements[i].addEventListener("click", function () {
          var rightSideContact = document.getElementsByClassName("right-side-contact")[0];
          var rightSidebarInfo = document.getElementsByClassName("right_sidebar_info")[0];
          var rightSideStar = document.getElementsByClassName("right_side_star")[0];
          var videoRightSidebar = document.getElementsByClassName("video-right-sidebar")[0];

          videoRightSidebar.classList.add("show-right-sidebar");
          videoRightSidebar.classList.remove("hide-right-sidebar");

          if (window.innerWidth > 991 && window.innerWidth < 1201) {
            var chatElements = document.getElementsByClassName("chat");
            for (var j = 0; j < chatElements.length; j++) {
              if (
                !chatElements[j].classList.contains("right-side-contact") &&
                !chatElements[j].classList.contains("right_side_star")
              ) {
                chatElements[j].style.marginLeft = "- chat_bar";
              }
            }
          }
          if (window.innerWidth < 992) {
            var chatElements = document.getElementsByClassName("chat");
            for (var k = 0; k < chatElements.length; k++) {
              if (
                !chatElements[k].classList.contains("right-side-contact") &&
                !chatElements[k].classList.contains("right_side_star")
              ) {
                chatElements[k].classList.add("hide-chatbar");
              }
            }
          }
        });
      }

      // Add an event listener for click on elements with class "close_profile"
      var closeProfileElements = document.getElementsByClassName("close_profile");
      for (var i = 0; i < closeProfileElements.length; i++) {
        closeProfileElements[i].addEventListener("click", function () {
          var rightSideContact = document.getElementsByClassName("right-side-contact")[0];
          var videoRightSidebar = document.getElementsByClassName("video-right-sidebar")[0];

          videoRightSidebar.classList.add("hide-right-sidebar");
          videoRightSidebar.classList.remove("show-right-sidebar");

          if (window.innerWidth > 991 && window.innerWidth < 1201) {
            var chatElements = document.getElementsByClassName("chat");
            for (var j = 0; j < chatElements.length; j++) {
              chatElements[j].style.marginLeft = "0";
            }
          }
          if (window.innerWidth < 992) {
            var chatElements = document.getElementsByClassName("chat");
            for (var j = 0; j < chatElements.length; j++) {
              chatElements[j].classList.remove("hide-chatbar");
            }
          }
        });
      }
    },
  },
};
</script>
